<template>
  <div
    :class="[
      inline ? 'inline-flex items-center gap-2' : 'w-full mb-2',
      { 'w-full': inline && fill },
    ]"
  >
    <div v-if="type == 'radio'">
      <div class="flex items-center">
        <input
          type="radio"
          :name="name"
          class="mx-2"
          :id="id"
          :checked="modelValue == value"
          :disabled="disabled"
          @input="$emit('update:modelValue', value)"
        />
        <label
          :for="id"
          class="inline-block cursor-pointer"
          :class="
            inline
              ? 'text-sm text-gray-700'
              : 'uppercase tracking-wide text-gray-700 text-xs font-bold'
          "
        >
          {{ displayLabel }}
        </label>
      </div>
    </div>
    <div v-if="type == 'checkbox'" class="flex items-start">
      <label
        class="inline-block uppercase tracking-wide text-gray-700 text-xs font-bold cursor-pointer"
      >
        <input
          type="checkbox"
          class="bg-gray-200 rounded border focus:border-gray-600 mr-1"
          :id="id"
          :checked="modelValue == (value || true)"
          :disabled="disabled"
          @input="$emit('update:modelValue', $event.target.checked && (value || true))"
        />
        <span :class="{ 'sr-only': hideLabel }">{{ displayLabel }}</span>
      </label>
    </div>
    <label v-if="inline && type !== 'checkbox' && type !== 'radio'" class="sr-only" :for="id">
      {{ displayLabel }}
    </label>
    <div
      class="flex items-begin justify-between"
      v-if="type !== 'checkbox' && type !== 'radio' && !inline"
    >
      <label
        class="inline-block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 cursor-pointer"
        :for="id"
        :class="{ 'sr-only': hideLabel }"
      >
        {{ displayLabel }}
      </label>
      <div
        title="Selecteer alle opties"
        v-if="type === 'multi' && modelValue?.length && options.length > 0 && options.length < 100"
      >
        <ArrowsPointingOutIcon
          v-if="options.length !== modelValue.length"
          class="w-4 h-4 border border-gray-300 rounded p-0.5 text-gray-500 hover:text-gray-700 hover:border-gray-600 cursor-pointer"
          @click="
            $emit(
              'update:modelValue',
              options.map((o) => o.value || o)
            )
          "
        />
        <XMarkIcon
          v-else
          class="w-4 h-4 border border-gray-300 rounded p-0.5 text-gray-500 hover:text-gray-700 hover:border-gray-600 cursor-pointer"
          @click="$emit('update:modelValue', [])"
        />
      </div>
    </div>

    <div
      v-if="type === 'week'"
      class="flex gap-2 items-center bg-white p-1 rounded border border-gray-300"
    >
      <div class="text-gray-600" :title="modelValue">week</div>
      <div>
        <input
          type="number"
          min="1"
          max="52"
          :value="DateTime.fromISO(modelValue).weekNumber"
          @input="updateWeekValue({ week: $event.target.value })"
          class="w-16 bg-gray-200 rounded text-sm text-gray-500 p-1 focus:bg-white border focus:border-gray-600 appearance-none focus:outline-none"
        />
      </div>
      <div>
        <select
          @input="updateWeekValue({ year: $event.target.value })"
          class="bg-gray-200 rounded text-sm text-gray-500 p-1 border"
        >
          <option
            v-for="year in years"
            :key="year"
            :value="year"
            :selected="(DateTime.fromISO(modelValue).weekYear || DateTime.now().weekYear) === year"
          >
            {{ year }}
          </option>
        </select>
      </div>
    </div>
    <div
      class="relative"
      v-if="
        !type ||
        type === 'number' ||
        type === 'date' ||
        type === 'time' ||
        type === 'text' ||
        type === 'email' ||
        type === 'url'
      "
    >
      <input
        :type="type || 'text'"
        :disabled="disabled"
        class="bg-gray-200 rounded text-gray-500 focus:bg-white border focus:border-gray-600 appearance-none focus:outline-none"
        :class="[
          inline ? 'text-sm p-1' : 'block w-full text-md p-2',
          inline && (fill ? 'w-full' : 'w-20'),
        ]"
        :value="value || modelValue"
        @input="$emit('update:modelValue', $event.target.value)"
        :id="id"
        :maxlength="maxLength"
        :min="minValue"
        :max="maxValue"
      />
      <Spinner v-if="loading" class="w-5 h-5 text-gray-500 absolute top-3 right-2" />
    </div>

    <textarea
      v-if="type === 'textarea'"
      :disabled="disabled"
      class="w-full bg-gray-200 rounded text-md text-gray-500 p-2 focus:bg-white border focus:border-gray-600 appearance-none focus:outline-none"
      :value="value || modelValue"
      @input="$emit('update:modelValue', $event.target.value)"
      :id="id"
    />
    <select
      v-else-if="type === 'select'"
      @change="$emit('update:modelValue', $event.target.value)"
      class="block w-full bg-gray-200 rounded text-md text-gray-500 text-md rounded p-2 overflow-hidden whitespace-nowrap"
      :class="[error && 'ring-1 ring-red-500']"
      :disabled="!options || disabled"
      :id="id"
    >
      <option
        :value="''"
        v-if="nullable || nullOption"
        :selected="modelValue == null || modelValue === ''"
      >
        {{ nullOption }}
      </option>
      <option
        v-for="option in listOptions"
        :value="option.value"
        :key="String(option.value)"
        :selected="modelValue == option.value"
      >
        {{ option.label }}
      </option>
    </select>
    <combo-box
      v-else-if="type == 'combo'"
      :model-value="modelValue"
      @update:modelValue="$emit('update:modelValue', $event)"
      :options="listOptions"
      :multiple="multiple"
      :disabled="disabled"
    />
    <multi-select
      v-else-if="type == 'multi'"
      :model-value="modelValue"
      @update:modelValue="$emit('update:modelValue', $event)"
      @search-change="$emit('search-change', $event)"
      :options="options"
      :placeholder="placeholder"
      :disabled="disabled"
      :mode="multiple === false ? 'single' : 'tags'"
      :loading="!!loading"
      searchable
      :no-options-text="noOptionsText"
      :open-direction="openDirection"
    />
    <div v-if="errorMessage" class="text-md text-red-500">
      {{ errorMessage }}
    </div>
    <div v-if="description && !inline" class="text-xs text-gray-500 mt-1">
      {{ description }}
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, toValue, watch } from 'vue'
import ComboBox from './ComboBox.vue'
import MultiSelect from './MultiSelect.vue'
import { DateTime } from 'luxon'
import { ArrowsPointingOutIcon } from '@heroicons/vue/24/outline'
import { XMarkIcon } from '@heroicons/vue/20/solid'
import Spinner from './Spinner.vue'

const props = defineProps({
  modelValue: null,
  label: null,
  options: null,
  type: null,
  minValue: null,
  maxValue: null,
  value: null,
  multiple: null,
  placeholder: null,
  nullable: null,
  nullOption: null,
  disabled: null,
  error: null,
  maxLength: null,
  checked: null,
  name: null,
  errorMessage: null,
  required: null,
  loading: null,
  noOptionsText: null,
  openDirection: null,
  // typed Boolean so a bare attribute means true instead of ""
  hideLabel: { type: Boolean, default: false },
  // helper text under the control
  description: { type: String, default: '' },
  // compact control in a row: no stacked label, small input
  inline: { type: Boolean, default: false },
  // inline fields are w-20 unless fill
  fill: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'search-change'])

// label + name + value, so radios in one group that share a label still get unique ids
const id = computed(() =>
  ['field', props.label, props.name, props.value]
    .filter((part) => part !== undefined && part !== null && part !== '')
    .join('-')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
)

const displayLabel = computed(() => {
  if (props.required && !props.label.includes('*')) {
    return props.label + ' *'
  }
  return props.label
})

const listOptions = computed(() => {
  const options = toValue(props.options)
  if (!options) return null
  if (options.length < 1) return options
  if (options[0].value || options[0].label) return options

  return options.map((o) => ({
    value: o,
    label: o,
  }))
})

function selectFirstOptionIfNeeded() {
  if (
    props.type === 'select' &&
    props.options?.length &&
    !props.nullable &&
    !props.nullOption &&
    !props.options.find((o) => o.value == props.modelValue)
  ) {
    emit('update:modelValue', props.options[0].value)
  }
}

const years = [DateTime.now().year - 1, DateTime.now().year, DateTime.now().year + 1]

function updateWeekValue({ week, year }) {
  if (week === '') {
    emit('update:modelValue', null)
  }

  if (!week && !year) return
  let date = props.modelValue ? DateTime.fromISO(props.modelValue) : DateTime.now()
  date = date.set({ weekday: 1 })

  if (week) {
    date = date.set({ weekNumber: Number(week) })
  } else if (year) {
    date = date.set({ weekYear: Number(year) })
  }

  emit('update:modelValue', date.toISODate())
}

// if not nullable and no option is selected, select the first option
watch(props, selectFirstOptionIfNeeded)
onMounted(selectFirstOptionIfNeeded)
</script>
